import { z } from 'zod'

import { useTranslation } from '@flecto/translations'
import { type UNSAFE_any, type CoreItemPriceRule } from '@flecto/types'

import { useTaxesInfo } from '../../../taxes'
import { type CoreItemForm } from '../../types/CoreItemForm'

type UseFormItemSchemaOptions = {
  item?: Partial<CoreItemForm>
}

const uniquePrice = (arr: UNSAFE_any[]) => {
  const uniquePrices = new Set<string>()
  for (const obj of arr) {
    const key = `${obj.duration_value}-${obj.duration_unit}-${obj.price_cents}`
    if (uniquePrices.has(key)) {
      return false
    }
    uniquePrices.add(key)
  }
  return true
}

export function useFormItemSchema(options?: UseFormItemSchemaOptions) {
  const { t } = useTranslation('saas', { keyPrefix: 'validation' })
  const { userDefaultTaxBracket, userDefaultTaxable, getCalculatedAmounts } =
    useTaxesInfo()

  const schema = z.object({
    title: z.string().min(2, { message: t('required') }),
    description: z.string().min(8, { message: t('required') }),
    category_id: z.string({ required_error: t('required') }),
    owner_address_id: z.string({ required_error: t('required') }),
    deposit_amount: z.number({ required_error: t('required') }),
    taxable: z.boolean({ required_error: t('required') }),
    tax_rate: z.string({ required_error: t('required') }),
    quantity: z.number({ required_error: t('required') }),
    on_store: z.boolean({ required_error: t('required') }),
    track_quantity: z.boolean({ required_error: t('required') }),
    allow_overbooking: z.boolean({ required_error: t('required') }),
    photos_attributes: z.array(
      z.object({
        main: z.boolean({ required_error: t('required') }),
        order: z.number({ required_error: t('required') }),
        photo_image_id: z.string().optional(),
        _destroy: z.boolean({ required_error: t('required') }),
      })
    ),
    pricing_rules: z
      .array(
        z
          .object({
            duration_value: z.number(),
            duration_unit: z.string(),
            price_cents: z
              .number()
              .min(1, {
                message: t('required'),
              })
              .refine(
                (value) => {
                  return value !== 0
                },
                { message: t('price_cents_value_min') }
              ),
          })
          .refine(
            (data) => {
              if (data.duration_unit === 'minutes') {
                return data.duration_value >= 15
              }
              return true
            },
            {
              message: t('duration_value_min', {
                replace: { number: 15 },
              }),
              path: ['duration_value'],
            }
          )
      )
      .refine(uniquePrice, { message: t('unique') }),
    pricing_rule_overtime: z.object({
      duration_unit: z.union([
        z.string({ required_error: t('required') }),
        z.undefined(),
      ]),
      duration_value: z.union([
        z.number({ required_error: t('required') }),
        z.undefined(),
      ]),
      price_cents: z.union([
        z.number().min(1, { message: t('price_cents_value_min') }),
        z.undefined(),
      ]),
    }),
  })

  const centsDefault = getCalculatedAmounts(
    1000000,
    userDefaultTaxBracket.value,
    userDefaultTaxable,
    true
  )

  const initialRule: CoreItemPriceRule = {
    duration_unit: 'days',
    duration_value: 1,
    price_cents: centsDefault.totalWithoutTaxes.cents,
  }

  const initialValues: CoreItemForm = {
    ...options?.item,
    internal_id: options?.item?.internal_id ?? undefined,
    category_id: String(options?.item?.category_id ?? ''),
    title: options?.item?.title ?? '',
    description: options?.item?.description ?? '',
    quantity: options?.item?.quantity ?? 1,
    on_store: options?.item?.on_store ?? false,
    allow_overbooking: options?.item?.allow_overbooking ?? false,
    track_quantity: options?.item?.track_quantity ?? false,
    pricing_rules: options?.item?.pricing_rules ?? [initialRule],
    overtime_enabled: options?.item?.overtime_enabled ?? false,
    pricing_rule_overtime:
      options?.item?.pricing_rule_overtime &&
      Object.keys(options?.item?.pricing_rule_overtime).length > 0
        ? options?.item?.pricing_rule_overtime
        : {},
    deposit_amount: options?.item?.deposit_amount ?? 0,
    tax_rate: options?.item?.tax_rate ?? userDefaultTaxBracket.value,
    taxable: options?.item?.taxable ?? userDefaultTaxable,
    photos_attributes: [],
    status: options?.item?.status ?? 'enabled',
    insured: options?.item?.insured ?? false,
  }

  return { schema, initialValues }
}
